import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ShellRail, useShellUI } from '../../shell/AppShell';
import MarketNav from './MarketNav';
import MarketLine from './MarketLine';
import MarketAvatar from './MarketAvatar';
import FillMoment from './FillMoment';
import ShareTicketModal from './ShareTicketModal';
import { pickOpenLine, pickCloseLine } from './fillCopy';
import {
  fetchAsset,
  placeOrder,
  cancelOrder,
  closePosition,
  watchAsset,
  unwatchAsset,
  fmtChips,
  fmtShares,
  shareWord,
  chipWord,
  fmtK,
  fmtPct,
  CHIPS_LABEL,
  CHART_RANGES,
  rangeCutoff,
  parseChipsInput,
  formatChipsInput,
} from './marketApi';
import './market.css';

const AMOUNTS = [5000, 10000, 25000];

const POSITION_NAMES = { QB: 'quarterback', RB: 'running back', WR: 'wide receiver', TE: 'tight end' };

/** Position-aware production rows from the nflverse season stats blob. */
function productionRows(position, s) {
  if (!s) return [];
  const rows = [];
  const add = (l, v) => {
    if (v !== null && v !== undefined && v !== '') rows.push({ l, v });
  };
  const pct = (v) => (v === null || v === undefined ? null : `${(v * 100).toFixed(1)}%`);
  const dec = (v, d = 1) => (v === null || v === undefined ? null : Number(v).toFixed(d));

  add('Games', s.games);
  if (position === 'QB') {
    add('Pass yards', s.passingYards ? s.passingYards.toLocaleString('en-US') : null);
    add('Pass TDs', s.passingTds);
    add('INTs', s.interceptions);
    add('CPOE', s.cpoe !== null && s.cpoe !== undefined ? `${dec(s.cpoe)}%` : null);
    add('EPA (pass)', dec(s.passingEpa));
    add('Rush yards', s.rushingYards);
    add('Rush TDs', s.rushingTds);
  } else if (position === 'RB') {
    add('Carries', s.carries);
    add('Rush yards', s.rushingYards ? s.rushingYards.toLocaleString('en-US') : null);
    add('Rush TDs', s.rushingTds);
    add('Targets', s.targets);
    add('Target share', pct(s.targetShare));
    add('Rec yards', s.receivingYards);
    add('Rec TDs', s.receivingTds);
    add('EPA (rush)', dec(s.rushingEpa));
  } else {
    add('Targets', s.targets);
    add('Target share', pct(s.targetShare));
    add('Air-yards share', pct(s.airYardsShare));
    add('WOPR', dec(s.wopr, 2));
    add('ADOT', dec(s.adot));
    add('Receptions', s.receptions);
    add('Rec yards', s.receivingYards ? s.receivingYards.toLocaleString('en-US') : null);
    add('Rec TDs', s.receivingTds);
    add('YAC per catch', dec(s.yacPerRec));
    add('Drops', s.drops);
  }
  return rows;
}

function aboutBlurb(player, keyStats) {
  const bits = [];
  const pos = POSITION_NAMES[player.position] || player.position;
  bits.push(
    `${pos.charAt(0).toUpperCase() + pos.slice(1)}${player.team ? `, ${player.team}` : ''}${
      keyStats.age ? `, ${keyStats.age} years old` : ''
    }.`
  );
  if (player.college) bits.push(`Out of ${player.college}.`);
  if (player.yearsExp === 0) bits.push('A rookie — no tape, all theory.');
  else if (player.yearsExp) bits.push(`${player.yearsExp} ${player.yearsExp === 1 ? 'season' : 'seasons'} in.`);
  if (player.injuryStatus) bits.push(`Currently listed ${player.injuryStatus} — the market may not be done pricing it.`);
  return bits.join(' ');
}

const MarketAsset = () => {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const { toast } = useShellUI();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [range, setRange] = useState('ALL');

  // Order ticket state
  const [side, setSide] = useState('buy');
  const [sellPct, setSellPct] = useState(100);
  const [customSellShares, setCustomSellShares] = useState('');
  const [amount, setAmount] = useState(10000);
  const [customAmount, setCustomAmount] = useState('');
  const [orderType, setOrderType] = useState('market');
  const [limitPrice, setLimitPrice] = useState('');
  const [ticketError, setTicketError] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // The trade-fill moment — replaces the ticket in place on a fill (see
  // FillMoment). Only closes (a realized result) ever offer a share.
  const [fillMoment, setFillMoment] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const fillNonceRef = useRef(0);
  const [tickClass, setTickClass] = useState('');
  const prevValueRef = useRef(null);

  // Any change to the order un-arms the confirm step.
  useEffect(() => {
    setConfirming(false);
  }, [side, amount, customAmount, orderType, limitPrice, sellPct, customSellShares]);

  const load = useCallback(() => {
    fetchAsset(playerId)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [playerId]);

  useEffect(() => {
    setData(null);
    setError(null);
    setTicketError(null);
    load();
    const poll = setInterval(load, 60000);
    return () => clearInterval(poll);
  }, [load]);

  // Flash the hero number green/red when a poll actually moves the price —
  // a tick should feel like a tick, not a silent re-render.
  useEffect(() => {
    if (!data || !data.player) return undefined;
    const val = data.player.value;
    const prev = prevValueRef.current;
    prevValueRef.current = val;
    if (prev === null || val === null || val === undefined || val === prev) return undefined;
    setTickClass(val > prev ? 'mk-tick-up' : 'mk-tick-down');
    const t = setTimeout(() => setTickClass(''), 900);
    return () => clearTimeout(t);
  }, [data]);

  const showCloseFill = (r, sharesClosed) => {
    fillNonceRef.current += 1;
    setFillMoment({
      nonce: fillNonceRef.current,
      tone: r.position.pnl >= 0 ? 'profit' : 'loss',
      title: 'Closed.',
      heroTarget: r.position.pnlPct,
      heroFormat: (v) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`,
      heroDecimals: 1,
      sub: `${fmtShares(sharesClosed)} sh · ${r.position.pnl >= 0 ? '+' : ''}${fmtChips(r.position.pnl)} ${chipWord(r.position.pnl)}`,
      line: pickCloseLine(r.position.pnl),
      positionId: r.position.id,
    });
  };

  const linePoints = useMemo(() => {
    if (!data) return [];
    const cutoff = rangeCutoff(range);
    if (!cutoff) return data.series;
    return data.series.filter((p) => p.date >= cutoff);
  }, [data, range]);

  if (error) {
    return (
      <div className="ft-view">
        <MarketNav />
        <p className="ft-sub">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="ft-view">
        <MarketNav />
        <div className="ft-loading">Pulling the tape</div>
      </div>
    );
  }

  const { player, series, keyStats, seasonStats, myPosition, myOrders, watching, tradable, buyingPower } = data;
  const production = seasonStats ? productionRows(player.position, seasonStats.stats) : [];
  const stake = customAmount ? parseInt(customAmount, 10) : amount;
  const priceBasis = orderType === 'limit' ? parseInt(limitPrice, 10) || null : player.value;
  const estShares = stake && priceBasis ? stake / priceBasis : null;
  const overBuyingPower = Boolean(stake && buyingPower !== null && buyingPower !== undefined && stake > buyingPower);

  // Selling what you own vs selling short.
  const ownedLong = myPosition && myPosition.side === 'long' ? myPosition : null;
  const sellingOwned = side === 'sell' && Boolean(ownedLong);
  const sellFraction = ownedLong
    ? Math.min(
        1,
        customSellShares && Number(customSellShares) > 0
          ? Number(customSellShares) / ownedLong.shares
          : sellPct / 100
      )
    : 0;
  const sellShares = ownedLong ? ownedLong.shares * sellFraction : 0;
  const sellProceeds = ownedLong ? Math.round(ownedLong.marketValue * sellFraction) : 0;

  const submitSell = () => {
    setTicketError(null);
    setPlacing(true);
    const stakeSlice = sellFraction >= 0.999 ? undefined : Math.max(1, Math.round(ownedLong.amount * sellFraction));
    closePosition(ownedLong.id, stakeSlice)
      .then((r) => {
        showCloseFill(r, sellShares);
        setCustomSellShares('');
        setSellPct(100);
        load();
      })
      .catch((e) => setTicketError(e.message))
      .finally(() => setPlacing(false));
  };
  const first = linePoints[0];
  const last = series[series.length - 1];
  const rangeChangePct = first && last && first.value ? ((last.value - first.value) / first.value) * 100 : null;

  const submitOrder = () => {
    setTicketError(null);
    setPlacing(true);
    placeOrder({
      playerId: player.id,
      action: side === 'sell' ? 'open_short' : 'open_long',
      type: orderType,
      amount: stake,
      limitPrice: orderType === 'limit' ? parseInt(limitPrice, 10) : undefined,
    })
      .then((r) => {
        if (r.position) {
          fillNonceRef.current += 1;
          setFillMoment({
            nonce: fillNonceRef.current,
            tone: 'you',
            title: 'Filled.',
            heroTarget: r.position.shares,
            heroFormat: (v) => `${fmtShares(v)} sh`,
            sub: `${fmtChips(r.position.amount)} ${chipWord(r.position.amount)}`,
            line: pickOpenLine(side === 'sell' ? 'short' : 'long'),
          });
        } else {
          toast('Resting until the market comes to it');
        }
        load();
      })
      .catch((e) => {
        if (e.message === 'guest_limit') {
          setTicketError('Guest limit reached. Log in with your Sleeper username to keep trading.');
        } else {
          setTicketError(e.message);
        }
      })
      .finally(() => setPlacing(false));
  };

  const act = (fn, okMsg) => () =>
    fn()
      .then(() => {
        toast(okMsg);
        load();
      })
      .catch((e) => toast(e.message));

  const stats = [
    { l: 'Age', v: keyStats.age },
    { l: 'Position rank', v: keyStats.positionRank },
    { l: '30-day trend', v: keyStats.trend30Pct !== null ? fmtPct(keyStats.trend30Pct) : null },
    { l: 'Period high', v: keyStats.high52 ? fmtChips(keyStats.high52) : null },
    { l: 'Period low', v: keyStats.low52 ? fmtChips(keyStats.low52) : null },
    { l: 'College', v: keyStats.college },
    { l: 'Drafted', v: keyStats.drafted },
    { l: 'Short interest', v: `${keyStats.shortInterest} ${keyStats.shortInterest === 1 ? 'desk' : 'desks'}` },
  ].filter((s) => s.v !== null && s.v !== undefined);

  return (
    <div className="ft-view">
      <Helmet>
        <title>{player.name} — The Market — Fantasy Taco</title>
      </Helmet>
      <MarketNav />

      <div className="mk-assethead">
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <MarketAvatar player={player} size={48} />
          <div>
          <p className="rh-label">
            {player.name}
            <span>
              {player.position} · {player.team}
            </span>
          </p>
          <div className={`rh-bignum${tickClass ? ` ${tickClass}` : ''}`}>
            {player.value ? fmtChips(player.value) : '—'}
            <small>ktc</small>
          </div>
          <div className="rh-delta">
            {rangeChangePct !== null && (
              <span className={rangeChangePct >= 0 ? 'mk-up' : 'mk-dn'}>
                {rangeChangePct >= 0 ? '▲' : '▼'} {fmtPct(rangeChangePct)}
              </span>
            )}{' '}
            <span className="lbl">over {range === 'ALL' ? 'all time' : range}</span>
          </div>
          </div>
        </div>
        <button
          type="button"
          className={`mk-watch${watching ? ' on' : ''}`}
          aria-pressed={watching}
          onClick={act(() => (watching ? unwatchAsset(player.id) : watchAsset(player.id)), watching ? 'Off the watchlist' : 'Watching')}
        >
          {watching && <span className="dot" aria-hidden="true" />}
          {watching ? 'Watching' : 'Watch'}
        </button>
      </div>

      <div className="rh-chart">
        <MarketLine
          points={linePoints}
          color="var(--ft-ink)"
          fill="rgba(29,29,31,0.04)"
          ariaLabel={`${player.name} KTC value history`}
        />
      </div>
      <div className="rh-ranges" role="group" aria-label="Chart range">
        {CHART_RANGES.map((r) => (
          <button
            key={r.key}
            type="button"
            className={`rh-range${range === r.key ? ' on' : ''}`}
            onClick={() => setRange(r.key)}
          >
            {r.key}
          </button>
        ))}
      </div>

      {(myPosition || myOrders.length > 0) && (
        <div className="rh-statcards">
          {myPosition && (
            <div className="ft-card white">
              <p className="h">Your position</p>
              <div className="v">
                {fmtShares(myPosition.shares)}{' '}
                <span style={{ fontSize: 13, color: 'var(--ft-ink-3)', fontWeight: 600 }}>
                  {shareWord(myPosition.shares)}
                </span>
              </div>
              <div className="row">
                <span>Side</span>
                <span className="mk-mono">
                  {myPosition.side.toUpperCase()} · in {fmtK(myPosition.entryValue)}
                </span>
              </div>
              <div className="row">
                <span>Value</span>
                <span className="mk-mono">
                  {fmtChips(myPosition.marketValue)} {chipWord(myPosition.marketValue)}
                </span>
              </div>
              <div className="row">
                <span>Return</span>
                <span className={`mk-mono ${myPosition.pnl >= 0 ? 'mk-up' : 'mk-dn'}`}>
                  {myPosition.pnl >= 0 ? '+' : ''}
                  {fmtChips(myPosition.pnl)} ({fmtPct(myPosition.pnlPct)})
                </span>
              </div>
              <div className="row">
                <span />
                {myPosition.side === 'long' ? (
                  <button
                    type="button"
                    style={{ fontSize: 12.5, color: 'var(--ft-brand-ink)', fontWeight: 600 }}
                    onClick={() => {
                      setSide('sell');
                      setFillMoment(null);
                    }}
                  >
                    Sell from the ticket
                  </button>
                ) : (
                  <button
                    type="button"
                    style={{ fontSize: 12.5, color: 'var(--ft-brand-ink)', fontWeight: 600 }}
                    onClick={() =>
                      closePosition(myPosition.id)
                        .then((r) => {
                          showCloseFill(r, myPosition.shares);
                          load();
                        })
                        .catch((e) => toast(e.message))
                    }
                  >
                    Cover the short
                  </button>
                )}
              </div>
            </div>
          )}
          {myOrders.map((o) => (
            <div className="ft-card white" key={o.id}>
              <p className="h">Your order</p>
              <div className="v">
                {o.amount ? fmtChips(o.amount) : '—'}{' '}
                <span style={{ fontSize: 13, color: 'var(--ft-ink-3)', fontWeight: 600 }}>
                  {o.action === 'close' ? 'close' : o.action === 'open_short' ? 'short' : 'buy'}
                  {o.type === 'limit' ? ' · limit' : ''}
                </span>
              </div>
              <div className="row">
                <span>Fills</span>
                <span className="mk-mono">
                  {o.type === 'limit' && o.limitPrice
                    ? `WHEN VALUE TOUCHES ${fmtK(o.limitPrice)}`
                    : 'TONIGHT AT THE CLOSE'}
                </span>
              </div>
              <div className="row">
                <span />
                <button
                  type="button"
                  style={{ fontSize: 12.5, color: 'var(--ft-ink-3)' }}
                  onClick={act(() => cancelOrder(o.id), 'Order cancelled')}
                >
                  Cancel
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="rh-sect">
        <h2>About</h2>
        <div className="rule" />
        <p className="rh-about">{aboutBlurb(player, keyStats)}</p>
      </div>

      {production.length > 0 && (
        <div className="rh-sect">
          <h2>Production · {seasonStats.season}</h2>
          <div className="rule" />
          <div className="rh-kstats">
            {production.map((s) => (
              <div className="rh-kstat" key={s.l}>
                <div className="l">{s.l}</div>
                <div className="v">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rh-sect">
        <h2>Key statistics</h2>
        <div className="rule" />
        <div className="rh-kstats">
          {stats.map((s) => (
            <div className="rh-kstat" key={s.l}>
              <div className="l">{s.l}</div>
              <div className="v">{s.v}</div>
            </div>
          ))}
        </div>
      </div>

      {tradable && (
        <>
          <div className="mk-tradebar">
            <button
              type="button"
              className="ft-btn"
              onClick={() => document.querySelector('.ft-sheettoggle')?.click()}
            >
              Trade
            </button>
          </div>
          <div className="mk-tradebar-spacer" aria-hidden="true" />
        </>
      )}

      <ShellRail>
        <div className="ft-railsec">
          {tradable ? (
            <div className="rh-ticket">
              <div className="rh-tickhead">
                <button
                  type="button"
                  className={`rh-ticktab${side === 'buy' ? ' on' : ''}`}
                  aria-pressed={side === 'buy'}
                  onClick={() => {
                    setSide('buy');
                    setFillMoment(null);
                  }}
                >
                  Buy
                </button>
                <button
                  type="button"
                  className={`rh-ticktab${side === 'sell' ? ' on' : ''}`}
                  aria-pressed={side === 'sell'}
                  onClick={() => {
                    setSide('sell');
                    setFillMoment(null);
                  }}
                >
                  {ownedLong ? 'Sell' : 'Short'}
                </button>
              </div>
              <div className="rh-tickbody">
                {fillMoment ? (
                  <FillMoment
                    nonce={fillMoment.nonce}
                    tone={fillMoment.tone}
                    title={fillMoment.title}
                    heroTarget={fillMoment.heroTarget}
                    heroFormat={fillMoment.heroFormat}
                    heroDecimals={fillMoment.heroDecimals || 0}
                    sub={fillMoment.sub}
                    line={fillMoment.line}
                    onShare={fillMoment.positionId ? () => setShareOpen(true) : undefined}
                    onDone={() => {
                      setFillMoment(null);
                      setShareOpen(false);
                    }}
                  />
                ) : sellingOwned ? (
                  <>
                    <div className="rh-tickrow">
                      <span className="l">You hold</span>
                      <span className="v mk-mono">
                        {fmtShares(ownedLong.shares)} SH · {fmtChips(ownedLong.marketValue)}
                      </span>
                    </div>
                    <div className="rh-tickrow" style={{ display: 'block' }}>
                      <span className="l" style={{ display: 'block', marginBottom: 8 }}>
                        Sell how much
                      </span>
                      <div className="rh-amounts">
                        {[25, 50, 100].map((p) => (
                          <button
                            key={p}
                            type="button"
                            className={`ft-fpill${!customSellShares && sellPct === p ? ' on' : ''}`}
                            onClick={() => {
                              setSellPct(p);
                              setCustomSellShares('');
                            }}
                          >
                            {p === 100 ? 'All' : `${p}%`}
                          </button>
                        ))}
                      </div>
                      <input
                        className="rh-custom"
                        inputMode="decimal"
                        placeholder="Custom shares"
                        value={customSellShares}
                        onChange={(e) => setCustomSellShares(e.target.value.replace(/[^0-9.]/g, ''))}
                        aria-label="Shares to sell"
                      />
                    </div>
                    <div className="rh-tickrow" style={{ borderTop: '1px solid var(--ft-hairline)' }}>
                      <span className="l">Selling</span>
                      <span className="v mk-mono">{fmtShares(sellShares)} SH</span>
                    </div>
                    <div className="rh-tickrow">
                      <span className="l">Proceeds</span>
                      <span className="v mk-mono">
                        {fmtChips(sellProceeds)} {chipWord(sellProceeds)}
                      </span>
                    </div>
                    <div className="rh-tickrow">
                      <span className="l">Fills</span>
                      <span className="v">Now</span>
                    </div>
                    {ticketError && <div className="rh-tickerr">{ticketError}</div>}
                    <button
                      type="button"
                      className="ft-btn"
                      style={{ width: '100%', justifyContent: 'center', marginTop: 10 }}
                      disabled={placing || sellFraction <= 0}
                      onClick={() => {
                        if (!confirming) {
                          setConfirming(true);
                          return;
                        }
                        setConfirming(false);
                        submitSell();
                      }}
                    >
                      {placing
                        ? 'Sending'
                        : confirming
                          ? `Confirm — sell ${fmtShares(sellShares)} ${shareWord(sellShares)}`
                          : 'Sell now'}
                    </button>
                  </>
                ) : (
                  <>
                    {side === 'sell' && (
                      <p style={{ fontSize: 12.5, color: 'var(--ft-ink-2)', margin: '2px 0 8px', lineHeight: 1.45 }}>
                        You don't hold him, so this opens a short. You profit if his value falls — sized by
                        buying power, not shares.
                      </p>
                    )}
                    <div className="rh-tickrow">
                      <span className="l">Order type</span>
                      <span className="v">
                        {orderType === 'market' ? 'Market' : 'Limit'}
                        <button
                          type="button"
                          onClick={() => setOrderType(orderType === 'market' ? 'limit' : 'market')}
                        >
                          {orderType === 'market' ? 'Switch to limit' : 'Switch to market'}
                        </button>
                      </span>
                    </div>
                    {orderType === 'limit' && (
                      <div className="rh-tickrow">
                        <span className="l">{side === 'buy' ? 'Buy at or below' : 'Short at or above'}</span>
                        <input
                          className="rh-custom"
                          style={{ maxWidth: 110, marginTop: 0, textAlign: 'right' }}
                          inputMode="numeric"
                          placeholder={player.value ? String(player.value) : 'value'}
                          value={limitPrice}
                          onChange={(e) => setLimitPrice(e.target.value.replace(/[^0-9]/g, ''))}
                          aria-label="Limit price"
                        />
                      </div>
                    )}
                    <div className="rh-tickrow">
                      <span className="l">Buying power</span>
                      <span className="v mk-mono">
                        {buyingPower !== null && buyingPower !== undefined ? fmtChips(buyingPower) : '—'}
                      </span>
                    </div>
                    <div className="rh-tickrow" style={{ display: 'block' }}>
                      <span className="l" style={{ display: 'block', marginBottom: 8 }}>
                        Amount
                      </span>
                      <div className="rh-amounts">
                        {AMOUNTS.map((a) => (
                          <button
                            key={a}
                            type="button"
                            className={`ft-fpill${!customAmount && amount === a ? ' on' : ''}`}
                            onClick={() => {
                              setAmount(a);
                              setCustomAmount('');
                            }}
                          >
                            {fmtChips(a)}
                          </button>
                        ))}
                        <button
                          type="button"
                          className={`ft-fpill${customAmount && Number(customAmount) === buyingPower ? ' on' : ''}`}
                          disabled={!buyingPower}
                          onClick={() => setCustomAmount(String(Math.floor(buyingPower)))}
                        >
                          All
                        </button>
                      </div>
                      <input
                        className="rh-custom"
                        inputMode="numeric"
                        placeholder={`Custom ${CHIPS_LABEL}`}
                        value={formatChipsInput(customAmount)}
                        onChange={(e) => setCustomAmount(parseChipsInput(e.target.value))}
                        aria-label="Custom amount"
                      />
                      {overBuyingPower && (
                        <p className="rh-tickerr" style={{ margin: 0 }}>
                          More than your buying power ({fmtChips(buyingPower)}).
                        </p>
                      )}
                    </div>
                    <div className="rh-tickrow" style={{ borderTop: '1px solid var(--ft-hairline)' }}>
                      <span className="l">Estimated shares</span>
                      <span className="v mk-mono">{estShares ? fmtShares(estShares) : '—'}</span>
                    </div>
                    <div className="rh-tickrow">
                      <span className="l">Fills</span>
                      <span className="v">
                        {orderType === 'limit' ? 'The night it touches' : 'Now'}
                      </span>
                    </div>
                    {ticketError && (
                      <div className="rh-tickerr">
                        {ticketError}{' '}
                        {ticketError.startsWith('Guest limit') && (
                          <button
                            type="button"
                            style={{ color: 'var(--ft-brand-ink)', fontWeight: 600 }}
                            onClick={() => navigate('/login')}
                          >
                            Log in
                          </button>
                        )}
                      </div>
                    )}
                    <button
                      type="button"
                      className="ft-btn"
                      style={{ width: '100%', justifyContent: 'center', marginTop: 10 }}
                      disabled={placing || !stake || overBuyingPower || (orderType === 'limit' && !limitPrice)}
                      onClick={() => {
                        if (!confirming) {
                          setConfirming(true);
                          return;
                        }
                        setConfirming(false);
                        submitOrder();
                      }}
                    >
                      {placing
                        ? 'Sending'
                        : confirming
                          ? `Confirm — ${side === 'buy' ? 'buy' : 'short'} ${estShares ? `${fmtShares(estShares)} ${shareWord(estShares)}` : 'shares'}`
                          : orderType === 'limit'
                            ? 'Let it rest'
                            : side === 'buy'
                              ? 'Buy now'
                              : 'Short now'}
                    </button>
                  </>
                )}
                {confirming && !placing && (
                  <button
                    type="button"
                    style={{
                      width: '100%',
                      textAlign: 'center',
                      marginTop: 8,
                      fontSize: 12.5,
                      color: 'var(--ft-ink-3)',
                      padding: 6,
                    }}
                    onClick={() => setConfirming(false)}
                  >
                    Never mind
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="ft-card" style={{ padding: '16px 18px' }}>
              <p style={{ fontSize: 13.5, color: 'var(--ft-ink-2)', margin: 0 }}>
                No market on this player yet.
              </p>
            </div>
          )}
        </div>
      </ShellRail>

      <ShareTicketModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        positionId={fillMoment?.positionId}
        assetName={player.name}
        assetPath={`/market/p/${player.id}`}
        pnlPct={fillMoment?.heroTarget ?? 0}
      />
    </div>
  );
};

export default MarketAsset;
